/**
 * Customer-Facing Display (CFD) — T-021
 *
 * Opens in a second browser window/tab or dedicated display.
 * The POS page broadcasts cart state over BroadcastChannel ('pos-cfd').
 * This page listens and renders a real-time cart view for the customer.
 *
 * Usage: cashier clicks "Display" button in POS → opens /cfd in a new window.
 */
import { useEffect, useRef, useState } from 'react';
import { ShoppingCart, CheckCircle, Package } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

interface CfdCartItem {
  name: string;
  variantName?: string;
  quantity: number;
  unitPrice: number;
  discountAmount: number;
  lineTotal: number;
}

interface CfdState {
  items: CfdCartItem[];
  subtotal: number;
  orderDiscountAmount: number;
  /** Automatic promotions applied to the cart (POS-I58). */
  promotions?: Array<{ name: string; discountAmount: number }>;
  totalTax: number;
  grandTotal: number;
  customerName?: string;
  checkoutComplete?: boolean;
  tenantName?: string;
  tenantLogo?: string;
}

const CHANNEL = 'pos-cfd';
const LS_KEY = 'pos-cfd-state';
/** How long the thank-you screen stays after a sale (POS-I60). */
const THANK_YOU_HOLD_MS = 5000;

function getInitialState(): CfdState | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function CustomerFacingDisplayPage() {
  const [cfd, setCfd] = useState<CfdState | null>(getInitialState);
  const [showThankYou, setShowThankYou] = useState(false);
  const thankYouTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const thankYouActiveRef = useRef(false);

  useEffect(() => {
    const bc = new BroadcastChannel(CHANNEL);

    bc.onmessage = (event: MessageEvent<CfdState>) => {
      const state = event.data;
      // Persist to localStorage so the display survives a page refresh
      try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch { /* ignore */ }

      if (state.checkoutComplete) {
        setShowThankYou(true);
        setCfd(null);
        if (thankYouTimerRef.current) clearTimeout(thankYouTimerRef.current);
        thankYouActiveRef.current = true;
        thankYouTimerRef.current = setTimeout(() => {
          thankYouActiveRef.current = false;
          setShowThankYou(false);
        }, THANK_YOU_HOLD_MS);
      } else if (thankYouActiveRef.current && state.items.length === 0) {
        // POS-I60: the till broadcasts an empty cart right after the sale; keep the
        // thank-you on screen for its full hold instead of replacing it at once.
        setCfd(state);
      } else {
        if (thankYouTimerRef.current) clearTimeout(thankYouTimerRef.current);
        thankYouActiveRef.current = false;
        setShowThankYou(false);
        setCfd(state);
      }
    };

    return () => {
      bc.close();
      if (thankYouTimerRef.current) clearTimeout(thankYouTimerRef.current);
    };
  }, []);

  // ── Idle / waiting screen ───────────────────────────────────────────────────
  if (showThankYou) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-success to-success/80 flex flex-col items-center justify-center text-success-foreground select-none">
        <CheckCircle className="h-24 w-24 mb-6 opacity-90" />
        <p className="text-5xl font-bold mb-3">Thank You!</p>
        <p className="text-xl opacity-80">Your purchase is complete.</p>
      </div>
    );
  }

  if (!cfd || cfd.items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-card to-card/80 flex flex-col items-center justify-center text-foreground select-none">
        <div className="mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-foreground/10">
          <ShoppingCart className="h-12 w-12 opacity-60" />
        </div>
        <p className="text-3xl font-semibold mb-2">
          {cfd?.tenantName ?? 'Welcome'}
        </p>
        <p className="text-lg opacity-50">Waiting for items…</p>
        {/* Animated dots */}
        <div className="mt-8 flex gap-2">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-2.5 w-2.5 rounded-full bg-foreground/30 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    );
  }

  // POS-I119: everything the customer is not paying: line (bargaining) + order + promotion discounts.
  const youSaved =
    cfd.items.reduce((sum, item) => sum + (item.discountAmount || 0), 0) +
    (cfd.orderDiscountAmount || 0) +
    (cfd.promotions ?? []).reduce((sum, promo) => sum + promo.discountAmount, 0);

  // ── Active cart screen ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col select-none">
      {/* Header */}
      <header className="flex items-center justify-between px-8 py-4 bg-card shadow-lg">
        <div className="flex items-center gap-3">
          {cfd.tenantLogo ? (
            <img src={cfd.tenantLogo} alt="Logo" className="h-10 w-auto rounded" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
              <Package className="h-5 w-5" />
            </div>
          )}
          <span className="text-2xl font-bold">{cfd.tenantName ?? 'POS'}</span>
        </div>
        {cfd.customerName && (
          <div className="rounded-full bg-primary/30 border border-primary/40 px-4 py-1.5 text-base font-medium">
            👤 {cfd.customerName}
          </div>
        )}
      </header>

      {/* Cart items */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        <table className="w-full">
          <thead>
            <tr className="text-start text-base text-muted-foreground border-b border-border">
              <th className="pb-3 font-semibold">Item</th>
              <th className="pb-3 font-semibold text-center w-24">Qty</th>
              <th className="pb-3 font-semibold text-end w-36">Unit Price</th>
              <th className="pb-3 font-semibold text-end w-36">Amount</th>
            </tr>
          </thead>
          <tbody>
            {cfd.items.map((item, i) => (
              <tr key={i} className="border-b border-border/60 hover:bg-muted/40 transition-colors">
                <td className="py-4">
                  <p className="text-2xl font-semibold leading-tight">{item.name}</p>
                  {item.variantName && (
                    <p className="text-base text-muted-foreground mt-1">{item.variantName}</p>
                  )}
                  {item.discountAmount > 0 && (
                    <p className="text-base text-success-text mt-1">
                      Discount: −{formatCurrency(item.discountAmount)}
                    </p>
                  )}
                </td>
                <td className="py-4 text-center">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted text-xl font-bold">
                    {item.quantity}
                  </span>
                </td>
                <td className="py-4 text-end text-xl text-foreground">{formatCurrency(item.unitPrice)}</td>
                <td className="py-4 text-end text-xl font-bold">{formatCurrency(item.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals footer */}
      <footer className="px-8 py-6 bg-card shadow-[0_-4px_24px_rgba(0,0,0,0.3)]">
        <div className="flex justify-end">
          <div className="w-96 space-y-2">
            <div className="flex justify-between text-lg text-muted-foreground">
              <span>Subtotal</span>
              <span>{formatCurrency(cfd.subtotal)}</span>
            </div>
            {cfd.orderDiscountAmount > 0 && (
              <div className="flex justify-between text-lg text-success-text">
                <span>Discount</span>
                <span>−{formatCurrency(cfd.orderDiscountAmount)}</span>
              </div>
            )}
            {(cfd.promotions ?? []).map((promo, i) => (
              <div key={i} className="flex justify-between text-lg text-success-text">
                <span className="truncate pe-3">{promo.name}</span>
                <span>−{formatCurrency(promo.discountAmount)}</span>
              </div>
            ))}
            {cfd.totalTax > 0 && (
              <div className="flex justify-between text-lg text-muted-foreground">
                <span>Tax</span>
                <span>{formatCurrency(cfd.totalTax)}</span>
              </div>
            )}
            {youSaved > 0 && (
              <div className="flex justify-between text-lg font-semibold text-success-text">
                <span>You saved</span>
                <span>{formatCurrency(youSaved)}</span>
              </div>
            )}
            <div className="flex justify-between text-4xl font-bold text-foreground border-t border-border pt-3 mt-2">
              <span>Total</span>
              <span className="text-success-text">{formatCurrency(cfd.grandTotal)}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
